import { COLLECTION_CURSOR_DURATION, COLLECTION_CURSOR_FRAME_COUNT, createCollectionCursorFrames } from './collectionCursorFrames';

// Draw the preview's action cursor in the document. Changing native cursor
// image URLs can fall back to a hand even when computed styles look correct.
export function watchPreviewCollectionCursor(root) {
  const doc = root.ownerDocument;
  if (!doc.documentElement.classList.contains('home-glass-preview')) return () => {};
  const win = doc.defaultView;
  const pointer = win.matchMedia('(hover: hover) and (pointer: fine)');
  const motion = win.matchMedia('(prefers-reduced-motion: reduce)');
  const variants = new Map();
  const holder = doc.createElement('div');
  holder.innerHTML = createCollectionCursorFrames('expand')[0].svg;
  const cursor = holder.firstElementChild;
  cursor.classList.add('preview-collection-cursor');
  cursor.setAttribute('aria-hidden', 'true');
  cursor.style.cssText = 'position:fixed;left:0;top:0;width:32px;height:32px;z-index:2147483647;pointer-events:none;visibility:hidden;overflow:visible;';
  doc.body.appendChild(cursor);
  const group = cursor.querySelector('g');
  const paths = cursor.querySelectorAll('path');
  let target = null;
  let frame = 0;
  let started = 0;
  let lastPaint = '';

  function stop() {
    win.cancelAnimationFrame(frame);
    target?.style.removeProperty('--collection-toggle-cursor');
    target = null;
    lastPaint = '';
    cursor.style.visibility = 'hidden';
  }

  function paint(now) {
    if (!target?.isConnected || !pointer.matches || doc.hidden) return stop();
    const direction = target.dataset.collectionToggle;
    const axis = win.matchMedia('(min-width: 661px)').matches ? target.dataset.collectionAxis || 'vertical' : 'vertical';
    const theme = doc.body.classList.contains('dark-mode') ? 'dark' : 'light';
    const key = `${direction}-${axis}-${theme}`;
    const index = motion.matches ? 0 : Math.floor(((now - started) % COLLECTION_CURSOR_DURATION) / COLLECTION_CURSOR_DURATION * COLLECTION_CURSOR_FRAME_COUNT);
    if (lastPaint !== `${key}-${index}`) {
      if (!variants.has(key)) variants.set(key, createCollectionCursorFrames(direction, theme, axis));
      const svg = variants.get(key)[index].svg;
      const d = svg.match(/<path d="([^"]+)"/)[1];
      paths[0].setAttribute('d', d);
      paths[1].setAttribute('d', d);
      const rotation = axis === 'left' ? -45 : axis === 'right' ? 45 : 0;
      group.setAttribute('transform', `rotate(${rotation} 16 16)`);
      paths.forEach((path, i) => path.setAttribute('stroke', (i % 2 === 0) === (theme === 'light') ? '#FFFFFF' : '#000000'));
      lastPaint = `${key}-${index}`;
    }
    if (!motion.matches) frame = win.requestAnimationFrame(paint);
  }

  function move(event) {
    const next = event.target.closest('[data-collection-toggle]');
    if (!next || !root.contains(next) || event.pointerType === 'touch' || !pointer.matches) return stop();
    cursor.style.transform = `translate3d(${event.clientX - 16}px,${event.clientY - 16}px,0)`;
    if (next !== target) {
      stop();
      target = next;
      started = win.performance.now() - 320;
      paint(win.performance.now());
    }
    if (!target) return;
    cursor.style.visibility = 'visible';
    target.style.setProperty('--collection-toggle-cursor', 'none');
  }
  function leave(event) {
    if (target && !target.contains(event.relatedTarget)) stop();
  }
  root.addEventListener('pointerover', move);
  root.addEventListener('pointermove', move);
  root.addEventListener('pointerout', leave);
  win.addEventListener('blur', stop);
  doc.addEventListener('visibilitychange', stop);
  doc.addEventListener('scroll', stop, true);
  pointer.addEventListener('change', stop);
  motion.addEventListener('change', stop);
  return () => {
    stop();
    root.removeEventListener('pointerover', move);
    root.removeEventListener('pointermove', move);
    root.removeEventListener('pointerout', leave);
    win.removeEventListener('blur', stop);
    doc.removeEventListener('visibilitychange', stop);
    doc.removeEventListener('scroll', stop, true);
    pointer.removeEventListener('change', stop);
    motion.removeEventListener('change', stop);
    cursor.remove();
  };
}
